import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { CompanyFiscalProfile, TaxOperationListResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { BRAZIL_UFS, CRTS, TAX_REGIMES, type Crt, type TaxRegime } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { TaxOperationDialog } from "./TaxOperationDialog";
import { CRT_LABEL, DIRECTION_LABEL, TAX_REGIME_LABEL } from "./tax-labels";

type Status = "active" | "inactive" | "all";

/** Preferências › Fiscal › Naturezas de operação (T2.2, RF-VEN-04): perfil fiscal da empresa e lista de naturezas. */
export function TaxOperationsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("active");
  const [creating, setCreating] = useState(false);
  const [pages, setPages] = useState<string[]>([]);
  const key = ["tax-operations", companyId, "list", status, pages];
  const list = useQuery({
    queryKey: key,
    queryFn: async () => {
      // Recarrega as páginas já abertas (o cursor é por nome + id).
      let cursor: string | null = null;
      const data: TaxOperationListResult["data"] = [];
      for (let i = 0; i <= pages.length; i++) {
        const qs = new URLSearchParams({ status, limit: "50" });
        if (cursor) qs.set("cursor", cursor);
        const r: TaxOperationListResult = await api<TaxOperationListResult>(`/tax-operations?${qs}`);
        data.push(...r.data);
        cursor = r.nextCursor;
        if (!cursor) break;
      }
      return { data, nextCursor: cursor };
    },
  });
  const canCreate = can(perms, "tax_operations", "create");
  const canWizard = canCreate && can(perms, "companies", "update");
  return (
    <>
      <ContentHeader
        title="Naturezas de operação"
        breadcrumb={["Preferências", "Fiscal", "Naturezas de operação"]}
        actions={
          <>
            {canWizard && (
              <Link to="/preferencias/fiscal/naturezas/assistente" className="btn btn-outline-primary">
                Assistente de configuração
              </Link>
            )}
            {canCreate && (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Incluir natureza
              </button>
            )}
          </>
        }
      />
      <CompanyFiscalCard canEdit={can(perms, "companies", "update")} />
      <section className="box card mb-3" aria-labelledby="tax-ops-list">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <h2 id="tax-ops-list" className="h5 mb-0 me-auto">
              Naturezas
            </h2>
            <label htmlFor="tax-ops-status" className="visually-hidden">
              Situação
            </label>
            <select
              id="tax-ops-status"
              className="form-select form-select-sm w-auto"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as Status);
                setPages([]);
              }}
            >
              <option value="active">Ativas</option>
              <option value="inactive">Inativas</option>
              <option value="all">Todas</option>
            </select>
          </div>
          <QueryStatus loading={list.isLoading} error={list.error} />
          {list.data && list.data.data.length === 0 && (
            <p className="text-muted mb-0">
              Nenhuma natureza de operação. {canWizard ? "Use o assistente para criar as mais comuns." : ""}
            </p>
          )}
          {list.data && list.data.data.length > 0 && (
            <div className="table-responsive">
              <table
                className="table table-sm table-hover align-middle mb-0"
                aria-label="Naturezas de operação"
              >
                <thead>
                  <tr>
                    <th scope="col">Nome</th>
                    <th scope="col">Tipo</th>
                    <th scope="col" className="text-end">
                      Regras
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <Link to="/preferencias/fiscal/naturezas/$id" params={{ id: o.id }}>
                          {o.name}
                        </Link>
                        {o.isDefault && (
                          <span className="ms-2">
                            <StatusBadge tone="info">Padrão</StatusBadge>
                          </span>
                        )}
                      </td>
                      <td>{DIRECTION_LABEL[o.direction]}</td>
                      <td className="text-end">{o.rulesCount}</td>
                      <td>
                        <StatusBadge tone={o.active ? "success" : "secondary"}>
                          {o.active ? "Ativa" : "Inativa"}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {list.data?.nextCursor && (
            <button
              type="button"
              className="btn btn-link"
              onClick={() => setPages((p) => [...p, list.data!.nextCursor!])}
            >
              Carregar mais
            </button>
          )}
        </div>
      </section>
      {creating && (
        <TaxOperationDialog
          onClose={() => setCreating(false)}
          onSaved={(id) => navigate({ to: "/preferencias/fiscal/naturezas/$id", params: { id } })}
        />
      )}
    </>
  );
}

type ProfileForm = { uf: string; ie: string; crt: Crt; taxRegime: TaxRegime | "" };

/** UF, IE, CRT e regime: o motor de impostos precisa da UF de origem e do CRT. */
export function CompanyFiscalCard({ canEdit }: { canEdit: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["fiscal-company", companyId];
  const q = useQuery({ queryKey: key, queryFn: () => api<CompanyFiscalProfile>("/fiscal/company") });
  const [f, setF] = useState<ProfileForm>({ uf: "", ie: "", crt: 1, taxRegime: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data)
      setF({ uf: q.data.uf ?? "", ie: q.data.ie ?? "", crt: q.data.crt, taxRegime: q.data.taxRegime ?? "" });
  }, [q.data]);
  const save = useMutation({
    mutationFn: () =>
      api<CompanyFiscalProfile>("/fiscal/company", {
        method: "PUT",
        body: JSON.stringify({
          ...(f.uf ? { uf: f.uf } : {}),
          ie: f.ie,
          crt: f.crt,
          ...(f.taxRegime ? { taxRegime: f.taxRegime } : {}),
        }),
      }),
    onSuccess: (p) => {
      setErrors({});
      qc.setQueryData(key, p);
      void qc.invalidateQueries({ queryKey: ["fiscal-certificate", companyId] });
      toast("success", "Perfil fiscal salvo");
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      toast("danger", errorMessage(e));
    },
  });
  return (
    <section className="box card mb-3" aria-labelledby="tax-company">
      <div className="card-body">
        <h2 id="tax-company" className="h5">
          Perfil fiscal da empresa
        </h2>
        <QueryStatus loading={q.isLoading} error={q.error} />
        {q.data && (
          <>
            <p className="text-muted small">
              {q.data.legalName}. A UF é a origem das operações; o CRT define se as regras usam CST (regime
              normal) ou CSOSN (Simples Nacional).
            </p>
            {!q.data.uf && (
              <div className="alert alert-warning py-2" role="alert">
                Informe a UF da empresa para calcular impostos e usar o assistente.
              </div>
            )}
            <form
              className="row align-items-end"
              onSubmit={(e) => {
                e.preventDefault();
                if (canEdit) save.mutate();
              }}
            >
              <Field label="UF" required className="col-md-2" error={errors.uf}>
                <select
                  className="form-select"
                  value={f.uf}
                  disabled={!canEdit}
                  onChange={(e) => setF({ ...f, uf: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {BRAZIL_UFS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Inscrição estadual"
                className="col-md-3"
                error={errors.ie}
                hint="Somente números ou ISENTO."
              >
                <input
                  value={f.ie}
                  maxLength={20}
                  disabled={!canEdit}
                  onChange={(e) => setF({ ...f, ie: e.target.value })}
                />
              </Field>
              <Field label="CRT" required className="col-md-4" error={errors.crt}>
                <select
                  className="form-select"
                  value={f.crt}
                  disabled={!canEdit}
                  onChange={(e) => setF({ ...f, crt: Number(e.target.value) as Crt })}
                >
                  {CRTS.map((c) => (
                    <option key={c} value={c}>
                      {CRT_LABEL[c]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Regime tributário" className="col-md-3" error={errors.taxRegime}>
                <select
                  className="form-select"
                  value={f.taxRegime}
                  disabled={!canEdit}
                  onChange={(e) => setF({ ...f, taxRegime: e.target.value as TaxRegime | "" })}
                >
                  <option value="">Não informado</option>
                  {TAX_REGIMES.map((r) => (
                    <option key={r} value={r}>
                      {TAX_REGIME_LABEL[r]}
                    </option>
                  ))}
                </select>
              </Field>
              {canEdit && (
                <div className="col-12 mb-3">
                  <button type="submit" className="btn btn-outline-primary" disabled={save.isPending}>
                    Salvar perfil fiscal
                  </button>
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </section>
  );
}
